import React, { useState, useEffect } from 'react';
import { Scale, Chord, Note } from 'tonal';
import { modeChords } from '../utils/chordsHelpers';

interface ScaleSelectorProps {
  selectedKey: string;
  scale: string;
  setKey: (key: string) => void;
  setScale: (scale: string) => void;
  selectedChord: string;
  noteViewMode: 'letters' | 'scaleDegrees';
  setNoteViewMode: (mode: 'letters' | 'scaleDegrees') => void;
}

const keys = [
  'C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 
  'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B'
];

/*
'C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'E#', 'Fb', 
'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 
'Bb', 'B', 'B#', 'Cb'
*/


const scaleTypes = Object.keys(modeChords);

const ScaleSelector: React.FC<ScaleSelectorProps> = ({ selectedKey, scale, setKey, setScale, selectedChord, noteViewMode, setNoteViewMode }) => {
  const [scaleNotes, setScaleNotes] = useState<string[]>([]);
  const [chordNotes, setChordNotes] = useState<string[]>([]);
  const [rootNote, setRootNote] = useState<string>('');

  useEffect(() => {
    if (selectedKey && scale) {
      const scaleName = `${selectedKey} ${scale}`;
      const notes = Scale.get(scaleName).notes;
      setScaleNotes(notes || []);
    }
  }, [selectedKey, scale]);

  useEffect(() => {
    if (scale.toLowerCase() === 'chromatic' && noteViewMode !== 'letters') {
      setNoteViewMode('letters');
    }
  }, [scale, noteViewMode, setNoteViewMode]);

  useEffect(() => {
    if (selectedChord) {
      const chord = Chord.get(selectedChord);
      setChordNotes(chord.notes || []);
      setRootNote(chord.tonic || '');
    } else {
      setChordNotes([]);
      setRootNote('');
    }
  }, [selectedChord]);

  const outsideChordNotes = chordNotes.filter(note => 
    !scaleNotes.includes(note) && !scaleNotes.includes(Note.enharmonic(note))
  );


  const isNoteInChord = (note: string) => {
    return chordNotes.includes(note) || chordNotes.some(chordNote => Note.enharmonic(chordNote) === note);
  };

  return (
    <div className="form-control">
      <label className="label">
        <span className="label-text">Scale:</span>
      </label>
      <div className="flex space-x-2">
        <select
          className="select select-bordered flex-grow"
          value={selectedKey}
          onChange={(e) => setKey(e.target.value)}
        >
          {keys.map((keyOption, index) => (
            <option key={index} value={keyOption}>{keyOption}</option>
          ))}
        </select>
        <select
          className="select select-bordered flex-grow"
          value={scale}
          onChange={(e) => setScale(e.target.value)}
        >
          {scaleTypes.map((scaleType, index) => (
            <option key={index} value={scaleType}>{scaleType}</option>
          ))}
        </select>
      </div>
      <label className="label">
        <span className="label-text">View Notes As:</span>
      </label>
      <div className="flex space-x-2">
        <select
          className="select select-bordered flex-grow"
          value={scale.toLowerCase() === 'chromatic' ? 'letters' : noteViewMode}
          onChange={(e) => setNoteViewMode(e.target.value as 'letters' | 'scaleDegrees')}
          disabled={scale.toLowerCase() === 'chromatic'}
        >
          <option value="letters">Letters</option>
          <option value="scaleDegrees">Scale Degrees</option>
        </select>
      </div>
      <div className="mt-2">
        <label className="label">
          <span className="label-text">Notes in Scale:</span>
        </label>
        <div className="join">
          {scaleNotes.map((note, index) => (
            <div
              key={index}
              className={`btn join-item 
                ${
                note === rootNote ? 'font-bold underline' : ''
              } ${
                  isNoteInChord(note) ? ' btn-accent' : ''
              }`}
              // ${
              //   // isNoteInChord(note) 
              //   //   ? 'badge-primary' 
              //   //   : 'badge-outline'
              // }
            >
              {note}
            </div>
          ))}
          </div>
          <div className='join ml-2'>
                    {selectedChord && outsideChordNotes.length > 0 && (
            <div>
              {outsideChordNotes.map((note, index) => (
                <span key={index} className="btn join-item btn-disabled">
                  {note}{index === outsideChordNotes.length - 1 ? '' : ','}
                </span>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ScaleSelector;